<template>
  <div class="migration-page">
    <el-alert
      title="预转存阶段不会修改 sys_file.path，也不会删除旧 MinIO 文件。路径切换只允许在目标数据库中执行。"
      type="warning"
      :closable="false"
      show-icon
    />

    <el-row :gutter="12" class="status-row">
      <el-col
        v-for="item in statusCards"
        :key="item.label"
        :xs="12"
        :sm="8"
        :md="4"
      >
        <el-card shadow="never" class="status-card">
          <div class="status-label">{{ item.label }}</div>
          <div class="status-value">{{ item.value }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="operation-card">
      <div slot="header" class="card-header">
        <span>迁移控制</span>
        <el-tag :type="controlStatusType">{{
          status.controlStatus || "未初始化"
        }}</el-tag>
      </div>
      <div class="operation-buttons">
        <el-button size="small" @click="runPrecheck">启动前自检</el-button>
        <el-button size="small" @click="initialize">扫描/补充任务</el-button>
        <el-button
          size="small"
          type="primary"
          :disabled="status.grayRunning"
          @click="runGray"
        >
          {{ status.grayRunning ? "灰度执行中" : "灰度20条" }}
        </el-button>
        <el-button size="small" type="success" @click="start"
          >开始全量</el-button
        >
        <el-button size="small" type="warning" @click="pause">暂停</el-button>
        <el-button size="small" type="primary" @click="resume">继续</el-button>
        <el-button size="small" type="danger" plain @click="retryFailed">
          重试复制失败
        </el-button>
        <el-button
          size="small"
          type="danger"
          plain
          @click="retryVerificationFailed"
        >
          重试核验失败
        </el-button>
        <el-button
          size="small"
          type="primary"
          plain
          :disabled="status.verificationControlStatus === 'RUNNING'"
          @click="startVerification"
        >
          {{
            status.verificationControlStatus === "RUNNING"
              ? "全量核验中"
              : "开始全量核验"
          }}
        </el-button>
        <el-button
          size="small"
          type="primary"
          plain
          :disabled="status.verificationControlStatus === 'RUNNING'"
          @click="startIncrementalVerification"
        >
          核验新增/补传
        </el-button>
        <el-button size="small" @click="showVerification">迁移核验</el-button>
        <el-button size="small" @click="exportFailures"
          >导出失败Excel</el-button
        >
        <el-button
          size="small"
          type="danger"
          :disabled="!status.pathSwitchEnabled"
          @click="switchPaths"
        >
          启动路径切换
        </el-button>
      </div>

      <el-descriptions
        v-if="precheck"
        title="最近一次自检"
        :column="4"
        border
        size="small"
        class="precheck"
      >
        <el-descriptions-item
          v-for="(value, key) in precheck"
          :key="key"
          :label="precheckLabels[key] || key"
        >
          <el-tag
            v-if="typeof value === 'boolean'"
            :type="value ? 'success' : 'danger'"
            size="mini"
          >
            {{ value ? "通过" : "未通过" }}
          </el-tag>
          <span v-else>{{ formatPrecheckValue(key, value) }}</span>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <el-card shadow="never" class="record-card">
      <div slot="header" class="card-header">
        <span>迁移明细</span>
        <div>
          <el-select
            v-model="query.status"
            clearable
            size="small"
            placeholder="全部状态"
            style="width: 190px"
            @change="search"
          >
            <el-option
              v-for="item in statusOptions"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
          <el-button size="small" icon="el-icon-refresh" @click="refreshAll">
            刷新
          </el-button>
        </div>
      </div>

      <el-table
        v-loading="recordLoading"
        :data="records"
        border
        stripe
        height="420"
      >
        <el-table-column prop="file_id" label="文件ID" width="150" />
        <el-table-column prop="file_category" label="分类" width="220" />
        <el-table-column label="状态" width="150">
          <template slot-scope="scope">
            <el-tag :type="recordStatusType(scope.row.status)" size="mini">
              {{ scope.row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="source_path_masked"
          label="旧路径（脱敏）"
          min-width="150"
          show-overflow-tooltip
        />
        <el-table-column
          prop="target_key"
          label="OSS目标Key"
          min-width="280"
          show-overflow-tooltip
        />
        <el-table-column prop="retry_count" label="重试" width="70" />
        <el-table-column
          prop="error_stage"
          label="失败阶段"
          width="130"
          show-overflow-tooltip
        />
        <el-table-column
          prop="error_message"
          label="失败原因"
          min-width="220"
          show-overflow-tooltip
        />
        <el-table-column
          prop="verification_status"
          label="独立核验"
          width="140"
        />
        <el-table-column
          prop="verification_error"
          label="核验失败原因"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="showDetail(scope.row.file_id)">
              详情
            </el-button>
            <el-button type="text" @click="showFullPath(scope.row.file_id)">
              完整旧路径
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        :current-page="query.page + 1"
        :page-size="query.size"
        :page-sizes="[20, 50, 100, 200]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @current-change="changePage"
        @size-change="changeSize"
      />
    </el-card>

    <el-dialog
      title="迁移核验报告"
      :visible.sync="verificationVisible"
      width="720px"
    >
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item
          v-for="(value, key) in verification"
          :key="key"
          :label="verificationLabels[key] || key"
        >
          <span :class="{ 'danger-text': isVerificationDanger(key, value) }">
            {{ value }}
          </span>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="verificationVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="迁移记录详情" :visible.sync="detailVisible" width="680px">
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item
          v-for="(value, key) in detail"
          :key="key"
          :label="detailLabels[key] || key"
        >
          {{ value }}
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { saveAs } from "file-saver";
import {
  exportMigrationFailures,
  initializeMigration,
  pauseMigration,
  precheckMigration,
  queryFullSourcePath,
  queryMigrationDetail,
  queryMigrationRecords,
  queryMigrationStatus,
  queryVerificationReport,
  resumeMigration,
  retryFailedMigration,
  retryVerificationFailedMigration,
  runGrayMigration,
  startFullVerification,
  startIncrementalVerification as startIncrementalVerificationRequest,
  startMigration,
  startPathSwitch,
} from "@/api/fileMigration";

export default {
  name: "FileMigration",
  data() {
    return {
      timer: null,
      statusLoading: false,
      recordLoading: false,
      status: {
        statusCounts: {},
      },
      precheck: null,
      records: [],
      total: 0,
      query: {
        status: "",
        page: 0,
        size: 20,
      },
      verificationVisible: false,
      verification: {},
      detailVisible: false,
      detail: {},
      statusOptions: [
        "PENDING",
        "COPYING",
        "RETRY",
        "COPIED",
        "COPY_FAILED",
        "SWITCHED",
        "SWITCH_CONFLICT",
      ],
      precheckLabels: {
        moduleEnabled: "模块开关",
        workerEnabled: "Worker开关",
        tablesReady: "迁移表",
        sourceBaseUrlConfigured: "旧域名",
        tempDirectoryWritable: "临时目录",
        tempUsableSpaceBytes: "临时目录可用空间",
        tempSpaceEnough: "临时空间保护",
        ossReady: "OSS读写自检",
        passed: "整体结果",
      },
      verificationLabels: {
        taskCode: "任务编码",
        verificationControlStatus: "独立核验任务状态",
        currentScopeTotal: "当前应迁移总数",
        untrackedCurrentScope: "生产新增未入任务",
        tracked_total: "已进入迁移明细",
        copied_or_switched: "已复制或已切换",
        copy_failed: "复制失败",
        switch_conflict: "路径切换冲突",
        copied_integrity_mismatch: "复制完整性异常",
        expected_size_mismatch: "数据库大小差异",
        sys_file_missing: "sys_file记录缺失",
        current_source_path: "当前仍为旧路径",
        current_target_path: "当前已为OSS路径",
        current_other_path: "当前路径被其他业务修改",
        verificationPending: "独立核验待完成",
        verificationFailed: "独立核验失败",
        verificationPassed: "独立核验通过",
      },
      detailLabels: {
        fileId: "文件ID",
        fileCategory: "文件分类",
        status: "状态",
        sourcePath: "旧文件路径",
        targetKey: "OSS目标Key",
        expectedSize: "数据库记录大小",
        actualSourceSize: "源文件实际大小",
        retryCount: "重试次数",
        errorStage: "失败阶段",
        errorMessage: "失败原因",
        verificationStatus: "独立核验状态",
        verificationError: "独立核验失败原因",
        verifiedTime: "独立核验时间",
      },
    };
  },
  computed: {
    counts() {
      return this.status.statusCounts || {};
    },
    verificationCounts() {
      return this.status.verificationStatusCounts || {};
    },
    statusCards() {
      return [
        { label: "任务总数", value: this.formatNumber(this.status.total) },
        {
          label: "已复制",
          value: this.formatNumber(
            (this.counts.COPIED || 0) + (this.counts.SWITCHED || 0)
          ),
        },
        {
          label: "复制失败",
          value: this.formatNumber(this.counts.COPY_FAILED || 0),
        },
        {
          label: "已切换",
          value: this.formatNumber(this.counts.SWITCHED || 0),
        },
        {
          label: "切换冲突",
          value: this.formatNumber(this.counts.SWITCH_CONFLICT || 0),
        },
        {
          label: "独立核验失败",
          value: this.formatNumber(this.verificationCounts.VERIFY_FAILED || 0),
        },
        {
          label: "已处理容量",
          value: this.formatBytes(this.status.processedBytes || 0),
        },
      ];
    },
    controlStatusType() {
      const value = this.status.controlStatus;
      if (value === "COMPLETED" || value === "COPY_COMPLETED") return "success";
      if (value === "PAUSED" || value === "COMPLETED_WITH_ERRORS")
        return "warning";
      if (value === "RUNNING" || value === "SWITCHING") return "primary";
      return "info";
    },
  },
  created() {
    this.refreshAll();
    this.timer = window.setInterval(() => {
      if (
        this.status.controlStatus === "RUNNING" ||
        this.status.controlStatus === "SWITCHING" ||
        this.status.verificationControlStatus === "RUNNING" ||
        this.status.grayRunning
      ) {
        this.refreshAll(false);
      }
    }, 5000);
  },
  beforeDestroy() {
    if (this.timer) {
      window.clearInterval(this.timer);
    }
  },
  methods: {
    async refreshAll(showMessage) {
      await Promise.all([this.loadStatus(), this.loadRecords()]);
      if (showMessage) this.$message.success("迁移状态已刷新");
    },
    async loadStatus() {
      this.statusLoading = true;
      try {
        const response = await queryMigrationStatus();
        this.status = response.data || { statusCounts: {} };
      } finally {
        this.statusLoading = false;
      }
    },
    async loadRecords() {
      this.recordLoading = true;
      try {
        const response = await queryMigrationRecords(this.query);
        this.records = response.data.content || [];
        this.total = response.data.totalElements || 0;
      } finally {
        this.recordLoading = false;
      }
    },
    async runPrecheck() {
      const response = await precheckMigration();
      this.precheck = response.data;
      this.$message[response.data.passed ? "success" : "warning"](
        response.data.passed ? "自检通过" : "自检未通过，请检查红色项目"
      );
    },
    async initialize() {
      await this.confirmAction(
        "将按当前生产数据扫描并增量补充迁移任务，不会修改 sys_file.path。",
        "确认扫描"
      );
      await initializeMigration();
      this.$message.success("任务扫描完成");
      await this.refreshAll();
    },
    async runGray() {
      await this.confirmAction(
        "将自动选择普通文件10条、身份证人像面5条、国徽面5条，在后台执行预转存。",
        "确认灰度"
      );
      const response = await runGrayMigration();
      this.$message.success("已提交 " + response.data.selected + " 条灰度文件");
      await this.refreshAll();
    },
    async start() {
      await this.confirmAction(
        "启动前会执行自检和增量扫描，成功后后台将自动持续转存。",
        "确认开始"
      );
      const response = await startMigration();
      this.precheck = response.data;
      this.$message.success("全量预转存已启动");
      await this.refreshAll();
    },
    async pause() {
      await pauseMigration();
      this.$message.success("已暂停领取新任务，正在处理的单文件会安全完成");
      await this.refreshAll();
    },
    async resume() {
      await resumeMigration();
      this.$message.success("迁移已继续");
      await this.refreshAll();
    },
    async retryFailed() {
      await this.confirmAction(
        "仅重置 COPY_FAILED 复制失败记录，不会重试路径切换冲突。",
        "确认重试"
      );
      const response = await retryFailedMigration();
      this.$message.success("已重置 " + response.data + " 条复制失败记录");
      await this.refreshAll();
    },
    async retryVerificationFailed() {
      await this.confirmAction(
        "将把独立核验失败且尚未切换路径的文件放回复制队列。目标Key内容冲突时仍不会覆盖或删除。",
        "确认重试"
      );
      const response = await retryVerificationFailedMigration();
      this.$message.success("已重置 " + response.data + " 条核验失败记录");
      await this.refreshAll();
    },
    async showVerification() {
      const response = await queryVerificationReport();
      this.verification = response.data || {};
      this.verificationVisible = true;
    },
    async startVerification() {
      await this.confirmAction(
        "将对全部已复制OSS对象重新发送HEAD请求，逐条校验对象存在性、大小和SHA-256元数据。核验支持断点续跑。",
        "确认开始核验"
      );
      const response = await startFullVerification();
      this.$message.success(
        "已提交 " + response.data.verificationTotal + " 条对象进行独立核验"
      );
      await this.refreshAll();
    },
    async startIncrementalVerification() {
      await this.confirmAction(
        "只核验上次全量核验后新增、补传或核验失败的OSS对象，适合维护日最终增量完成后执行。",
        "确认增量核验"
      );
      const response = await startIncrementalVerificationRequest();
      this.$message.success(
        "已提交 " + response.data.verificationTotal + " 条对象进行增量核验"
      );
      await this.refreshAll();
    },
    async exportFailures() {
      const response = await exportMigrationFailures();
      const type =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      saveAs(new Blob([response.data], { type }), "历史文件迁移失败记录.xlsx");
    },
    async switchPaths() {
      await this.$confirm(
        "该操作会在目标数据库中批量修改 sys_file.path，启动后后台自动执行并可在重启后续跑。请确认旧生产已停止写入、数据库已完整恢复、当前库已标记 TARGET，且核验报告无异常。",
        "生产切换二次确认",
        {
          confirmButtonText: "确认启动路径切换",
          cancelButtonText: "取消",
          type: "error",
          dangerouslyUseHTMLString: false,
        }
      );
      await startPathSwitch();
      this.$message.success("路径切换任务已启动");
      await this.refreshAll();
    },
    async showDetail(fileId) {
      const response = await queryMigrationDetail(fileId);
      this.detail = response.data || {};
      this.detailVisible = true;
    },
    async showFullPath(fileId) {
      await this.confirmAction(
        "完整旧路径属于敏感信息，本次查看会记录操作日志。",
        "确认查看"
      );
      const response = await queryFullSourcePath(fileId);
      this.detail = response.data || {};
      this.detailVisible = true;
    },
    search() {
      this.query.page = 0;
      this.loadRecords();
    },
    changePage(page) {
      this.query.page = page - 1;
      this.loadRecords();
    },
    changeSize(size) {
      this.query.size = size;
      this.query.page = 0;
      this.loadRecords();
    },
    confirmAction(message, confirmButtonText) {
      return this.$confirm(message, "操作确认", {
        confirmButtonText,
        cancelButtonText: "取消",
        type: "warning",
      });
    },
    recordStatusType(status) {
      if (status === "COPIED" || status === "SWITCHED") return "success";
      if (status === "COPY_FAILED" || status === "SWITCH_CONFLICT")
        return "danger";
      if (status === "COPYING" || status === "RETRY") return "warning";
      return "info";
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    formatBytes(value) {
      const bytes = Number(value || 0);
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
      if (bytes < 1024 * 1024 * 1024) {
        return (bytes / 1024 / 1024).toFixed(2) + " MB";
      }
      return (bytes / 1024 / 1024 / 1024).toFixed(2) + " GB";
    },
    formatPrecheckValue(key, value) {
      if (key === "tempUsableSpaceBytes") return this.formatBytes(value);
      return value;
    },
    isVerificationDanger(key, value) {
      const dangerKeys = [
        "untrackedCurrentScope",
        "copy_failed",
        "switch_conflict",
        "copied_integrity_mismatch",
        "verificationPending",
        "verificationFailed",
        "sys_file_missing",
        "current_other_path",
      ];
      return dangerKeys.indexOf(key) !== -1 && Number(value || 0) > 0;
    },
  },
};
</script>

<style scoped>
.migration-page {
  padding: 16px;
}

.status-row,
.operation-card,
.record-card {
  margin-top: 14px;
}

.status-card {
  margin-bottom: 12px;
}

.status-label {
  color: #909399;
  font-size: 13px;
}

.status-value {
  margin-top: 8px;
  color: #303133;
  font-size: 22px;
  font-weight: 600;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.operation-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.operation-buttons .el-button + .el-button {
  margin-left: 0;
}

.precheck {
  margin-top: 18px;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

.danger-text {
  color: #f56c6c;
  font-weight: 600;
}
</style>
